import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

const pageLayoutVariants = cva('mx-auto flex w-full flex-col', {
  variants: {
    variant: {
      default: 'max-w-7xl gap-8 pt-14 px-6 pb-6 md:p-6 lg:p-8',
      /**
       * Full-bleed immersive pages (thread, dashboards, twin chat). Desktop
       * fills the viewport. On mobile the app shell still paints `MobileNavBar`
       * (`h-14`) above this layout, so `h-dvh` here is 56px taller than the
       * remaining column and the docked composer slips under the fold until
       * you scroll. Subtract that bar only below `md`, where the bar actually
       * exists. `--feedback-manual-lane` is 0 unless a manual feedback card is
       * open on a dense surface; then it is the strip above this page.
       */
      fullBleed:
        'max-w-none gap-0 p-0 min-h-0 overflow-hidden h-[calc(100dvh-3.5rem-var(--feedback-manual-lane))] md:h-[calc(100dvh-var(--feedback-manual-lane))]',
      /** Wide layout for immersive content pages — fills available width with standard px-8 padding, no max-width cap */
      wide: 'max-w-none gap-8 pt-14 px-4 md:pt-0 md:px-8',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

export interface PageLayoutProps
  extends React.HTMLAttributes<HTMLDivElement>,
  VariantProps<typeof pageLayoutVariants> { }

export const PageLayout = React.forwardRef<HTMLDivElement, PageLayoutProps>(
  ({ className, variant, ...props }, ref) => (
    <div ref={ref} className={cn(pageLayoutVariants({ variant }), className)} {...props} />
  ),
)
PageLayout.displayName = 'PageLayout'
